import { useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { adminApi, type AdminProtocol } from "@/lib/api";
import { useDataTable } from "@/hooks/use-data-table";
import { DataTable } from "@/components/ui";
import { Button } from "@/components/ui/button";
import type { ColumnDef, RowAction, BulkAction } from "@/components/ui";
import { Trash2, RotateCcw, Pencil, Download, Building2 } from "lucide-react";
import { AdminProtocolDetailModal } from "./AdminProtocolDetailModal";
import { downloadCsv, formatTime } from "@/lib/utils";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

const STATUS_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "draft", label: "Utkast" },
  { value: "finalized", label: "Slutfört" },
];

const TYPE_OPTIONS = [
  { value: "", label: "Alla typer" },
  { value: "annual_meeting", label: "Årsstämma" },
  { value: "board_meeting", label: "Styrelsemöte" },
  { value: "constitutive_board", label: "Konstituerande styrelsemöte" },
];

const statusBadgeStyles: Record<string, string> = {
  draft: "bg-yellow-100 text-yellow-700",
  finalized: "bg-green-100 text-green-700",
};

const statusLabels: Record<string, string> = {
  draft: "Utkast",
  finalized: "Slutfört",
};

const typeLabels: Record<string, string> = {
  annual_meeting: "Årsstämma",
  board_meeting: "Styrelsemöte",
  constitutive_board: "Konstituerande styrelsemöte",
};

const typeBadgeStyles: Record<string, string> = {
  annual_meeting: "bg-blue-100 text-blue-700",
  board_meeting: "bg-gray-100 text-gray-700",
  constitutive_board: "bg-purple-100 text-purple-700",
};

export function AdminProtocols() {
  const [selectedProtocolId, setSelectedProtocolId] = useState<string | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const {
    data,
    isLoading,
    search,
    setSearch,
    filters,
    setFilter,
    pagination,
    setPage,
    setPageSize,
    refresh,
    sortColumnId,
    sortDirection,
    handleSort,
    resetFilters,
  } = useDataTable<AdminProtocol, { status: string; type: string; meetingDate: string }>({
    fetcher: (params) =>
      adminApi.listProtocols({
        page: params.page,
        pageSize: params.pageSize,
        search: params.search,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        status: params.status || undefined,
        type: params.type || undefined,
        meetingDateFrom: params.meetingDate || undefined,
        meetingDateTo: params.meetingDate || undefined,
      }),
    pageSize: 25,
    initialFilters: { status: "", type: "", meetingDate: "" },
    sortKeyMap: {
      companyName: "companyName",
      type: "type",
      meetingDate: "meetingDate",
      status: "status",
      createdAt: "createdAt",
    },
  });

  const handleDelete = useCallback(
    async (protocol: AdminProtocol) => {
      if (protocol.status === "finalized") {
        toast.error("Kan inte ta bort slutförda protokoll direkt. Återställ först till utkast.");
        return;
      }
      if (!confirm("Är du säker på att du vill ta bort detta protokoll?")) return;
      try {
        const response = await adminApi.deleteProtocol(protocol.id);
        if (response.success) {
          refresh();
          toast.success("Protokollet har tagits bort");
        }
      } catch {
        toast.error("Kunde inte ta bort protokollet");
      }
    },
    [refresh]
  );

  const handleUnfinalize = useCallback(
    async (protocol: AdminProtocol) => {
      if (protocol.status !== "finalized") {
        toast.error("Endast slutförda protokoll kan återställas till utkast");
        return;
      }
      if (!confirm("Är du säker på att du vill återställa detta protokoll till utkast? Det kan påverka kopplad årsredovisning.")) return;
      try {
        const response = await adminApi.unfinalizeProtocol(protocol.id);
        if (response.success) {
          refresh();
          toast.success("Protokollet har återställts till utkast");
        }
      } catch {
        toast.error("Kunde inte återställa protokollet");
      }
    },
    [refresh]
  );

  const columns: ColumnDef<AdminProtocol>[] = useMemo(
    () => [
      {
        id: "companyName",
        header: "Företag",
        sortable: true,
        sortAccessor: (row) => row.companyName,
        filter: {
          type: "text" as const,
          value: search,
          onChange: setSearch,
          placeholder: "Sök företag eller org.nr...",
        },
        cell: (row) => (
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-gray-400" />
            <div>
              <p className="text-sm font-medium text-gray-900">{row.companyName}</p>
              <p className="text-xs text-gray-500">{row.organizationNumber}</p>
            </div>
          </div>
        ),
      },
      {
        id: "type",
        header: "Typ",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => row.type,
        filter: {
          type: "select" as const,
          value: filters.type,
          onChange: (v: string) => setFilter("type", v),
          options: TYPE_OPTIONS,
        },
        cell: (row) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeBadgeStyles[row.type] || "bg-gray-100 text-gray-700"}`}
          >
            {typeLabels[row.type] || row.type}
          </span>
        ),
      },
      {
        id: "meetingDate",
        header: "Mötesdatum",
        sortable: true,
        sortAccessor: (row) => new Date(row.meetingDate),
        filter: {
          type: "date" as const,
          value: filters.meetingDate,
          onChange: (v: string) => setFilter("meetingDate", v),
        },
        cell: (row) => (
          <div className="text-sm">
            <span className="text-gray-900">{new Date(row.meetingDate).toLocaleDateString("sv-SE")}</span>
            {row.meetingTime && (
              <span className="text-gray-500 ml-1">{formatTime(row.meetingTime)}</span>
            )}
          </div>
        ),
      },
      {
        id: "chairperson",
        header: "Ordförande",
        hideBelow: "lg",
        cell: (row) => (
          <span className="text-sm text-gray-600">
            {row.chairpersonName || "-"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        sortAccessor: (row) => row.status,
        filter: {
          type: "select" as const,
          value: filters.status,
          onChange: (v: string) => setFilter("status", v),
          options: STATUS_OPTIONS,
        },
        cell: (row) => (
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeStyles[row.status] || "bg-gray-100 text-gray-700"}`}
          >
            {statusLabels[row.status] || row.status}
          </span>
        ),
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "lg",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
    ],
    [search, setSearch, filters.type, filters.meetingDate, filters.status, setFilter]
  );

  const rowActions: RowAction<AdminProtocol>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Visa detaljer",
        icon: Pencil,
        onClick: (row) => setSelectedProtocolId(row.id),
      },
      {
        key: "unfinalize",
        label: "Återställ till utkast",
        icon: RotateCcw,
        variant: "default",
        onClick: handleUnfinalize,
        visible: (row) => row.status === "finalized",
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleDelete,
        visible: (row) => row.status === "draft",
      },
    ],
    [handleDelete, handleUnfinalize]
  );

  const handleBulkDelete = useCallback(
    async (protocols: AdminProtocol[]) => {
      const deletableProtocols = protocols.filter((p) => p.status === "draft");
      if (deletableProtocols.length === 0) {
        toast.error("Inga protokoll kan tas bort (endast utkast kan raderas)");
        return;
      }
      if (
        !confirm(
          `Är du säker på att du vill ta bort ${deletableProtocols.length} protokoll?`
        )
      ) {
        return;
      }
      setIsBulkDeleting(true);
      try {
        let successCount = 0;
        let errorCount = 0;
        for (const protocol of deletableProtocols) {
          try {
            const response = await adminApi.deleteProtocol(protocol.id);
            if (response.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } catch {
            errorCount++;
          }
        }
        if (successCount > 0) {
          toast.success(`${successCount} protokoll har tagits bort`);
          setSelectedKeys(new Set());
          refresh();
        }
        if (errorCount > 0) {
          toast.error(`Kunde inte ta bort ${errorCount} protokoll`);
        }
      } finally {
        setIsBulkDeleting(false);
      }
    },
    [refresh]
  );

  const handleBulkExport = useCallback((protocols: AdminProtocol[]) => {
    downloadCsv(
      protocols,
      [
        { header: "ID", accessor: (r) => r.id },
        { header: "Företag", accessor: (r) => r.companyName },
        { header: "Org.nummer", accessor: (r) => r.organizationNumber },
        { header: "Typ", accessor: (r) => typeLabels[r.type] || r.type },
        { header: "Mötesdatum", accessor: (r) => new Date(r.meetingDate).toLocaleDateString("sv-SE") },
        { header: "Ordförande", accessor: (r) => r.chairpersonName || "" },
        { header: "Status", accessor: (r) => statusLabels[r.status] || r.status },
        { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
      ],
      `protokoll-${new Date().toISOString().slice(0, 10)}.csv`
    );
    toast.success(`${protocols.length} protokoll exporterade`);
  }, []);

  const bulkActions: BulkAction<AdminProtocol>[] = useMemo(
    () => [
      {
        key: "export",
        label: "Exportera",
        icon: Download,
        onClick: handleBulkExport,
      },
      {
        key: "delete",
        label: "Ta bort",
        icon: Trash2,
        variant: "danger",
        onClick: handleBulkDelete,
        loading: isBulkDeleting,
      },
    ],
    [handleBulkExport, handleBulkDelete, isBulkDeleting]
  );

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.protocols.title} noIndex />
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Protokoll</h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">Hantera alla protokoll (årsstämma, styrelsemöten)</p>
          </div>
          <Button
            className="w-full sm:w-auto"
            variant="outline"
            size="sm"
            onClick={() =>
              downloadCsv(
                data,
                [
                  { header: "ID", accessor: (r) => r.id },
                  { header: "Företag", accessor: (r) => r.companyName },
                  { header: "Org.nummer", accessor: (r) => r.organizationNumber },
                  { header: "Typ", accessor: (r) => typeLabels[r.type] || r.type },
                  { header: "Mötesdatum", accessor: (r) => new Date(r.meetingDate).toLocaleDateString("sv-SE") },
                  { header: "Ordförande", accessor: (r) => r.chairpersonName || "" },
                  { header: "Status", accessor: (r) => statusLabels[r.status] || r.status },
                  { header: "Skapad", accessor: (r) => new Date(r.createdAt).toLocaleDateString("sv-SE") },
                ],
                `protokoll-alla-${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
            disabled={data.length === 0}
          >
            <Download size={14} />
            Exportera CSV
          </Button>
        </div>

        <DataTable
          columns={columns}
          data={data}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          emptyMessage="Inga protokoll hittades"
          pagination={pagination}
          onPageChange={setPage}
          pageSizeOptions={[25, 50, 100, 250]}
          onPageSizeChange={setPageSize}
          onRowClick={(row) => setSelectedProtocolId(row.id)}
          rowActions={rowActions}
          serverSortColumnId={sortColumnId}
          serverSortDirection={sortDirection}
          onSort={handleSort}
          onResetFilters={resetFilters}
          selectable
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          bulkActions={bulkActions}
        />

        {selectedProtocolId && (
          <AdminProtocolDetailModal
            protocolId={selectedProtocolId}
            onClose={() => setSelectedProtocolId(null)}
            onUpdated={refresh}
          />
        )}
      </div>
    </>
  );
}
